import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { sortBySearchMatch } from "@ckgo2/shared";
import {
  Sparkles,
  Users,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  ChevronRight,
  Loader2,
  Wand2,
} from "lucide-react";
import { formatDate } from "@/lib/format";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { formatMoney } from "@/lib/format";
import ConfirmModal from "@/components/common/ConfirmModal";

interface Segment {
  id: string;
  name: string;
  description?: string;
  aiPrompt: string;
  generatedSql: string;
  segmentType: "manual" | "automatic";
  customerCount: number;
  lastGeneratedAt?: string;
  isActive: boolean;
  createdAt: string;
}

interface Customer {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  totalSpent?: number;
  tripsCount?: number;
}

interface SegmentPreview {
  generatedSql: string;
  description: string;
  customers: Customer[];
  customerCount: number;
}

/**
 * AI segments tab (moved from the retired /segments page — block 4.15 řez 3). Natural-language
 * prompt → SQL segment; segments narrow the audience in the Send dialog.
 */
export default function SegmentsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);

  const { data: segmentsData, isLoading } = useQuery({
    queryKey: ["segments"],
    queryFn: () => api.get<ApiResponse<Segment[]>>("/api/admin/segments"),
  });

  const segments = segmentsData?.data || [];
  // Hledání filtruje i řadí podle shody s názvem, shoda jen v zadání segmentu jde za ní.
  const filteredSegments = sortBySearchMatch(segments, search, {
    name: (s) => s.name,
    other: (s) => [s.aiPrompt],
    tieBreak: () => 0,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/segments/${id}`),
    onSuccess: () => {
      toast.success(t("newsletter.segments.deleted"));
      queryClient.invalidateQueries({ queryKey: ["segments"] });
    },
    onError: () => {
      toast.error(t("newsletter.segments.deleteFailed"));
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<ApiResponse<Segment>>(`/api/admin/segments/${id}/regenerate`),
    onSuccess: () => {
      toast.success(t("newsletter.segments.regenerated"));
      queryClient.invalidateQueries({ queryKey: ["segments"] });
    },
    onError: () => {
      toast.error(t("newsletter.segments.regenerateFailed"));
    },
  });

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div className="border-primary-200 bg-primary-50 flex items-start justify-between gap-4 rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="text-primary-600 h-5 w-5" />
          <p className="text-primary-700 text-sm">{t("newsletter.segments.intro")}</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary whitespace-nowrap">
          <Wand2 className="h-5 w-5" />
          {t("newsletter.segments.newSegment")}
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder={t("newsletter.segments.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10"
        />
      </div>

      <div className="card">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
          </div>
        ) : filteredSegments.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center text-gray-500">
            <Users className="mb-2 h-8 w-8 text-gray-300" />
            <p>{t("newsletter.segments.noSegments")}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-primary-600 mt-2 text-sm hover:underline"
            >
              {t("newsletter.segments.createFirst")}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredSegments.map((segment) => (
              <div key={segment.id} className="flex items-center justify-between py-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium text-gray-900">{segment.name}</h3>
                    <span className="badge bg-primary-100 text-primary-700">
                      {t("newsletter.shared.customersCount", { count: segment.customerCount })}
                    </span>
                    {segment.segmentType === "automatic" && (
                      <span className="badge bg-accent-100 text-accent-700">
                        {t("newsletter.segments.automatic")}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{segment.aiPrompt}</p>
                  {segment.lastGeneratedAt && (
                    <p className="mt-1 text-xs text-gray-500">
                      {t("newsletter.segments.lastUpdated", {
                        date: formatDate(segment.lastGeneratedAt),
                      })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => regenerateMutation.mutate(segment.id)}
                    disabled={regenerateMutation.isPending}
                    className="btn-ghost btn-sm"
                    title={t("newsletter.segments.regenerateTitle")}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${regenerateMutation.isPending ? "animate-spin" : ""}`}
                    />
                  </button>
                  <button
                    onClick={() => setSelectedSegment(segment)}
                    className="btn-ghost btn-sm"
                    title={t("newsletter.segments.viewCustomers")}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(segment.id)}
                    className="btn-ghost btn-sm text-error-600 hover:bg-error-50"
                    title={t("newsletter.segments.deleteTitle")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && <CreateSegmentModal onClose={() => setShowCreateModal(false)} />}
      {selectedSegment && (
        <SegmentDetailModal segment={selectedSegment} onClose={() => setSelectedSegment(null)} />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete);
          setConfirmDelete(null);
        }}
        isLoading={deleteMutation.isPending}
        title={t("newsletter.segments.deleteTitle")}
        message={t("newsletter.segments.confirmDeleteMessage")}
        confirmText={t("common.actions.delete")}
        variant="danger"
      />
    </div>
  );
}

function CreateSegmentModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [preview, setPreview] = useState<SegmentPreview | null>(null);

  const previewMutation = useMutation({
    mutationFn: (promptText: string) =>
      api.post<ApiResponse<SegmentPreview>>("/api/admin/segments/preview", {
        prompt: promptText,
      }),
    onSuccess: (data) => {
      setPreview(data.data || null);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("newsletter.segments.previewFailed"));
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; prompt: string }) =>
      api.post<ApiResponse<Segment>>("/api/admin/segments", data),
    onSuccess: () => {
      toast.success(t("newsletter.segments.created"));
      queryClient.invalidateQueries({ queryKey: ["segments"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || t("newsletter.segments.createFailed"));
    },
  });

  const handlePreview = () => {
    if (!prompt.trim()) {
      toast.error(t("newsletter.segments.enterDescription"));
      return;
    }
    previewMutation.mutate(prompt);
  };

  const handleCreate = () => {
    if (!name.trim()) {
      toast.error(t("newsletter.segments.enterName"));
      return;
    }
    if (!prompt.trim()) {
      toast.error(t("newsletter.segments.enterDescription"));
      return;
    }
    createMutation.mutate({ name, prompt });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="text-xl font-bold text-gray-900">{t("newsletter.segments.newSegment")}</h2>
        <p className="mt-1 text-sm text-gray-600">{t("newsletter.segments.describeHint")}</p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="label">{t("newsletter.segments.nameLabel")}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("newsletter.segments.namePlaceholder")}
              className="input"
            />
          </div>

          <div>
            <label className="label">{t("newsletter.segments.promptLabel")}</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t("newsletter.segments.promptPlaceholder")}
              rows={3}
              className="input"
            />
            <p className="mt-1 text-xs text-gray-500">{t("newsletter.segments.tips")}</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handlePreview}
              disabled={previewMutation.isPending}
              className="btn-ghost flex-1"
            >
              {previewMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Search className="h-5 w-5" />
              )}
              {t("common.actions.preview")}
            </button>
            <button
              onClick={handleCreate}
              disabled={createMutation.isPending || !preview}
              className="btn-primary flex-1"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Plus className="h-5 w-5" />
              )}
              {t("newsletter.segments.createSegment")}
            </button>
          </div>

          {preview && (
            <div className="mt-4 rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">
                  {t("newsletter.segments.previewTitle")}
                </h3>
                <span className="badge bg-success-100 text-success-700">
                  {t("newsletter.shared.customersCount", { count: preview.customerCount })}
                </span>
              </div>
              {preview.description && (
                <p className="mt-2 text-sm text-gray-600">{preview.description}</p>
              )}
              {preview.customers.length > 0 && (
                <div className="mt-3 max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="pb-2 text-left font-medium text-gray-500">
                          {t("common.fields.email")}
                        </th>
                        <th className="pb-2 text-left font-medium text-gray-500">
                          {t("common.fields.name")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.customers.slice(0, 10).map((customer) => (
                        <tr key={customer.id}>
                          <td className="py-2 text-gray-900">{customer.email}</td>
                          <td className="py-2 text-gray-600">
                            {customer.firstName} {customer.lastName}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.customers.length > 10 && (
                    <p className="mt-2 text-center text-xs text-gray-500">
                      {t("newsletter.segments.moreCustomers", {
                        count: preview.customers.length - 10,
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">
            {t("common.actions.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SegmentDetailModal({ segment, onClose }: { segment: Segment; onClose: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["segment", segment.id],
    queryFn: () =>
      api.get<ApiResponse<{ segment: Segment; customers: Customer[]; total: number }>>(
        `/api/admin/segments/${segment.id}/customers`,
      ),
  });

  const customers = data?.data?.customers || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{segment.name}</h2>
            <p className="mt-1 text-sm text-gray-600">{segment.aiPrompt}</p>
          </div>
          <span className="badge bg-primary-100 text-primary-700">
            {t("newsletter.shared.customersCount", { count: segment.customerCount })}
          </span>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
            </div>
          ) : customers.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-gray-500">
              {t("newsletter.segments.noCustomers")}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    {t("common.fields.email")}
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    {t("common.fields.name")}
                  </th>
                  <th className="pb-3 text-right text-sm font-medium text-gray-500">
                    {t("newsletter.segments.totalSpent")}
                  </th>
                  <th className="pb-3 text-right text-sm font-medium text-gray-500">
                    {t("newsletter.segments.tripsCol")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td className="py-3 text-gray-900">{customer.email}</td>
                    <td className="py-3 text-gray-600">
                      {customer.firstName} {customer.lastName}
                    </td>
                    <td className="py-3 text-right text-gray-600">
                      {formatMoney(customer.totalSpent || 0)}
                    </td>
                    <td className="py-3 text-right text-gray-600">{customer.tripsCount || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-ghost">
            {t("common.actions.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
